import React, { Dispatch, useRef, useState } from 'react';
import StyledModal from '../../../NewUI/Components/GenericModal/StyledModal';
import { emailIsValid } from '../../../NewUI/Components/Utilities/emailIsValid';
import { Checkbox, FormControlLabel, Stack } from '@mui/material';
import MultiTagInput from '../../../NewUI/Components/CustomUIElements/MultiTagInput';
import { sharedClasses } from '../../../NewUI/Components/CustomUIElements/sharedClasses';
import ModalFooterButtons from '../../../NewUI/Components/GenericModal/ModalFooterButtons';
import { ApplicationState, ApplicationAction, IApplication } from '../types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ModalType } from '../config';
import { styles } from './styles';
import { MultilineFormTextField } from '../../Components/CustomUIElements/FormTextField';
import Api from '../API';
import { formatUserPermissions } from '../../Components/Utilities/formatUserPermissions';

export default function EmailExternal({
  ApplicationState,
  dispatch,
  aiSummariesEnabled
}: {
  ApplicationState: ApplicationState;
  dispatch: Dispatch<ApplicationAction>;
  aiSummariesEnabled: boolean;
}) {
  const [emails, setEmails] = useState<string[]>([]);
  const [submissionError, setSubmissionError] = useState<string>('');
  const [includeAttachments, setIncludeAttachments] = useState<boolean>(false);
  const [includeSmartForms, setIncludeSmartForms] = useState<boolean>(false);
  const [includeSummary, setIncludeSummary] = useState<boolean>(false);
  const [includeSubmissionDate, setIncludeSubmissionDate] = useState<boolean>(false);
  const [includeLinkToRecruit, setIncludeLinkToRecruit] = useState<boolean>(false);
  const [includeGender, setIncludeGender] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>('');
  const emailInputRef = useRef<HTMLDivElement>(null);
  const { modalsOpen } = ApplicationState;
  const queryClient = useQueryClient();
  const application = queryClient.getQueryData<IApplication>(['application']);
  const permissions = formatUserPermissions();

  const showGenderCheckbox =
    !!Number(application?.job.options.gender_field) && permissions?.Applications?.['Show Gender'];

  const checkboxOptions = [
    {
      id: 'attachments',
      label: 'Include Attachments',
      checked: includeAttachments,
      onChange: setIncludeAttachments
    },
    {
      id: 'smartForms',
      label: 'Include Smart Forms',
      checked: includeSmartForms,
      onChange: setIncludeSmartForms
    },
    {
      id: 'summary',
      label: 'Include Felix AI Summary',
      checked: includeSummary,
      onChange: setIncludeSummary,
      condition: aiSummariesEnabled
    },
    {
      id: 'submissionDate',
      label: 'Include Submission Date',
      checked: includeSubmissionDate,
      onChange: setIncludeSubmissionDate
    },
    {
      id: 'linkToRecruit',
      label: 'Include link to :Recruit',
      checked: includeLinkToRecruit,
      onChange: setIncludeLinkToRecruit
    },
    ...(showGenderCheckbox
      ? [
          {
            id: 'gender',
            label: 'Include gender',
            checked: includeGender,
            onChange: setIncludeGender
          }
        ]
      : [])
  ];

  const renderCheckbox = (option: (typeof checkboxOptions)[0]) => (
    <FormControlLabel
      key={option.id}
      control={
        <Checkbox
          sx={sharedClasses.formCheckbox}
          onChange={(e) => option.onChange(e.target.checked)}
          value={option.checked}
        />
      }
      label={option.label}
      sx={{
        ...sharedClasses.checkboxLabel,
        '.MuiFormControlLabel-label': { fontSize: '14px' }
      }}
    />
  );

  const handleClose = () => {
    dispatch({ type: 'SET_MODALS_OPEN', payload: null });
  };

  const { mutate: sendEmail, isLoading: sendingEmail } = useMutation({
    mutationFn: async () => {
      if (application) {
        const { res } = await Api.sendExternalEmails(application.job.id, {
          application_ids: [application.id],
          external_view: {
            emails: emails.join(','),
            full_view: includeAttachments,
            smart_forms: includeSmartForms,
            include_summary: includeSummary,
            include_submission_date: includeSubmissionDate,
            include_application_link: includeLinkToRecruit,
            include_gender: includeGender,
            notes
          }
        });
        return res;
      }
    },
    onSuccess: (res) => {
      dispatch({ type: 'SET_SNACKBAR', payload: { message: res.message, state: 'success' } });
      handleClose();
    },
    onError: (e: { errors: string; status: number }) =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message:
            e.errors ??
            'Error sending emails, please try again or contact support if the issue persists',
          state: 'error'
        }
      })
  });

  return (
    <StyledModal
      isOpen={modalsOpen === ModalType.EMAIL_EXTERNAL}
      handleClose={handleClose}
      label="External Email"
      styleOverrides={styles.modalStyleOverrides}
    >
      <Stack sx={styles.modalContainer}>
        <Stack sx={styles.modalTitle}>Email external</Stack>
        <Stack sx={{ rowGap: 2, overflow: 'auto' }}>
          <Stack sx={{ fontSize: '18px', fontWeight: 'bold', color: '#666666' }}>
            Recipient details
          </Stack>
          <Stack ref={emailInputRef}>
            <MultiTagInput
              tags={emails}
              setTags={setEmails}
              validation={emailIsValid}
              validationMessage="Must input valid email"
              label="Email address/s"
              width="100%"
              error={submissionError}
              tagType="emails"
              helperText="To copy and paste bulk emails, email addresses must be separated with a comma"
              required
            />
            <Stack sx={{ flexDirection: 'row', columnGap: 2, marginLeft: '3px', flexWrap: 'wrap' }}>
              {checkboxOptions
                .filter((option) => option.condition === undefined || option.condition)
                .map(renderCheckbox)}
            </Stack>
          </Stack>
          <MultilineFormTextField
            label="Notes"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            styles={{ marginLeft: '3px', paddingRight: '4px' }}
          />
        </Stack>
        <ModalFooterButtons
          primaryButtonText="Send"
          primaryButtonCallback={() =>
            emails.length ? sendEmail() : setSubmissionError('Must input at least one valid email')
          }
          secondaryButtonText="Cancel"
          secondaryButtonCallback={handleClose}
          isLoading={sendingEmail}
          primaryButtonMinWidth="75px"
        />
      </Stack>
    </StyledModal>
  );
}
